import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { getInstitutionOffer, subscribeProductFilterOptions } from '@adornis/bh-registration/api/institution-offer.js';
import {
  institutionOfferContext,
  productFilterContext,
  productFilterOptionsContext,
} from '@adornis/bh-registration/client/bh-registration-controller.js';
import { getColor } from '@adornis/bh-registration/client/helper/getColor.js';
import { productListingPageBuildifyPreset } from '@adornis/buchungsheld-db/_buildify-presets.js';
import { ProductFilter } from '@adornis/buchungsheld-db/entities/products.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import '@adornis/calendar/calendar.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import '@adornis/chemistry/elements/components/x-progress-bar.js';
import '@adornis/chemistry/elements/components/x-toggle-content.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import '@adornis/forms/x-checkbox.js';
import '@adornis/forms/x-toggle-button.js';
import { provideContext } from '@adornis/functional-lit/directive-provide-context.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { combineLatest, distinctUntilChanged, filter, map, of, switchMap } from 'rxjs';
import '../../components/bh-expandable-content.js';
import { BHRegistrationFlowSubPage } from './bh-page-registration-sub-page.js';
import { RegistrationDetailsBackButton } from './functional-components/RegistrationDetailsBackButton.js';
import { RegistrationLecturerDetails } from './functional-components/RegistrationLecturerDetails.js';
import { RegistrationProductDetails } from './functional-components/RegistrationProductDetails.js';

@customElement('bh-page-registration-workshop')
export class BHPageRegistrationWorkshop extends BHRegistrationFlowSubPage {
  @state() private readonly _registrationData = new RXController(
    this,
    this._registrationController$.pipe(switchMap(regController => regController?.registrationData$ ?? of(null))),
  );

  private queryParamFilter = routingState.searchParams.asObservable().pipe(
    map(searchParams => {
      return ProductFilter.fromQueryParamItems('f_', [...searchParams.entries()]);
    }),
    distinctUntilChanged((prev, curr) => {
      return JSON.stringify((prev as any).doc) === JSON.stringify((curr as any).doc);
    }),
  );

  filter = new RXController(
    this,
    combineLatest([this._registrationController$, this.queryParamFilter]).pipe(
      filter(([registrationController, queryParamFilter]) => !!registrationController && !!queryParamFilter),
      map(([registrationController, queryParamFilter]) => [registrationController!, queryParamFilter!] as const),
      switchMap(([registrationController, queryParamFilter]) =>
        registrationController.categorieConstraints$.pipe(
          map(categoryConstraints => [registrationController, queryParamFilter, categoryConstraints] as const),
        ),
      ),
      map(([registrationController, queryParamFilter, categoryConstraints]) => {
        const filterData = {
          ...queryParamFilter.doc,
          institutionIDs: [registrationController.institutionID],
        };

        if (categoryConstraints.length > 0) {
          filterData.categoryIDs = categoryConstraints;
        }

        return new ProductFilter(filterData);
      }),
    ),
  );

  @state()
  private readonly institutionOffer = new RXController(
    this,
    this.filter.observable.pipe(
      filter(isDefined),
      switchMap(filter => {
        return getInstitutionOffer(filter)({
          institutionID: 1,
          categories: { _id: 1, name: 1 },
          products: { _id: 1, categoryID: 1 },
          workshopSets: { _id: 1, categoryID: 1, workshopIDs: 1 },
        });
      }),
    ),
  );

  @state()
  private readonly productFilterOptions = new RXController(
    this,
    this._registrationController$.pipe(
      filter(isDefined),
      switchMap(registrationController =>
        combineLatest([
          registrationController.institutionID$.pipe(filter(isDefined)),
          registrationController.categorieConstraints$,
        ]),
      ),
      switchMap(([institutionID, categoryConstraints]) => {
        // NOTE: do not reuse productFilter, this filter should match all products that could potentially be shown
        const filter = new ProductFilter({ institutionIDs: [institutionID] });
        if (categoryConstraints) filter.categoryIDs = categoryConstraints;

        return subscribeProductFilterOptions(filter)({
          timeframes: { name: 1, from: 1, till: 1, publishDate: 1 },
          categories: { _id: 1, name: 1 },
          topics: 1,
        });
      }),
    ),
  );

  override nextAction = () => this._registrationController.next();

  // eslint-disable-next-line max-lines-per-function
  override content() {
    this.nextButtonDisabled = !this._registrationData.value?.productData.length;
    if (!this.filter.value) {
      console.warn('no filter');
      return nothing;
    }

    return html`
      <x-flex
        ${provideContext(institutionOfferContext, this.institutionOffer.value)}
        ${provideContext(productFilterContext, this.filter.value)}
        ${provideContext(productFilterOptionsContext, this.productFilterOptions.value)}
        @update-filter=${(e: ValueEvent<{ key: string; value: any }>) => {
          const { key, value } = e.detail.value;
          this.updateFilter(key, value);
        }}
        @onShowWorkshopDetail=${e => {
          console.log('workshopID: ', e.detail);
          const productID = e.detail.workshop._id;
          XDialog.showPopup({
            content: (_res, _rej, ref) => html`
              <x-flex ${css({ padding: `${this.spacing.md}`, gap: `${this.spacing.md}` })}>
                ${RegistrationDetailsBackButton(() => ref.close(null))} ${RegistrationProductDetails({ productID })}
              </x-flex>
            `,
          });
        }}
        @onShowLecturerDetail=${e => {
          const lecturerID = e.detail.lecturer._id;
          XDialog.showPopup({
            content: (_res, _rej, ref) => html`
              <x-flex ${css({ padding: `${this.spacing.md}`, gap: `${this.spacing.md}` })}>
                ${RegistrationDetailsBackButton(() => ref.close(null))} ${RegistrationLecturerDetails({ lecturerID })}
              </x-flex>
            `,
          });
        }}
      >
        ${this._registrationController.institution?.maximumParallelSignups
          ? html`
              <x-text
                ${css({
                  padding: `${this.spacing.xs}`,
                  borderRadius: this.sizes.borderRadius,
                  textAlign: 'center',
                  background:
                    this._registrationController.numSignups >=
                    this._registrationController.institution.maximumParallelSignups
                      ? this.colors.tone.error.shade(300)
                      : this.colors.accent.shade(200),
                  color:
                    this._registrationController.numSignups >=
                    this._registrationController.institution.maximumParallelSignups
                      ? this.colors.on.tone.error.shade(300).toString()
                      : this.colors.on.accent.shade(200).toString(),
                })}
              >
                ${this._registrationController.numSignups} /
                ${this._registrationController.institution.maximumParallelSignups}
                ${this._translation.translate('termin', 'plural')}
              </x-text>
            `
          : nothing}
        ${(() => {
          const institution = this._registrationController.institution;
          const hasDesign = typeof institution.productPage === 'string';
          if (hasDesign) return unsafeHTML(institution.productPage);
          else return unsafeHTML(productListingPageBuildifyPreset);
        })()}
      </x-flex>
    `;
  }

  updateFilter(key, value) {
    if (value === null || value === undefined) {
      routingState.searchParams.delete('f_' + key!);
      routingState.searchParams.delete('f_' + key! + '[]');
    } else if (Array.isArray(value)) routingState.searchParams.set('f_' + key! + '[]', value.join(';'));
    else routingState.searchParams.set('f_' + key!, value!);
  }

  override styles() {
    const institution = this._registrationController.institution;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!institution) return [];

    return [
      ...super.styles(),
      {
        '.timer-flex': {
          padding: this.spacing.sm,
          borderRadius: this.sizes.borderRadius,
          // background: this.colors.tone.black,
          color: this.colors.tone.black,
          gap: this.spacing.xs,
          border: `1px solid ${this.colors.tone.black}`,
        },
        'tr-chip': {
          background: getColor(
            institution.settings.colorDefinitions?.registrationColorDefinitions?.chipInfosColor,
            institution.settings.inheritedColorDefinitions?.value?.registrationColorDefinitions?.chipInfosColor,
            this.colors.accent,
          ),
          color: getColor(
            this._registrationController.institution?.settings.colorDefinitions?.registrationColorDefinitions
              ?.chipInfosFontColor,
            this._registrationController.institution?.settings.inheritedColorDefinitions?.value
              ?.registrationColorDefinitions.chipInfosFontColor,
            '#fff',
          ),
        },
        'tr-basic-card[data-selected="1"]': {
          outline: '5px solid',
          outlineColor: getColor(
            institution.settings.colorDefinitions?.registrationColorDefinitions?.stepColor,
            institution.settings.inheritedColorDefinitions?.value?.registrationColorDefinitions?.stepColor,
            this.colors.accent,
          ),
          outlineOffset: '-5px',
        },
      },
    ];
  }
}
